import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import keycloak from './keycloak';
import { decodeJwt } from './jwtDecode';
import { useNavigate } from 'react-router-dom';
import { fetchMenu } from '../redux/slice/sidebarSlice';
import { fetchLocationValues } from '../redux/slice/locationSlice';
import { fetchLovValues } from '../redux/slice/lovSlice';
import { fetchCategoryValues } from '../redux/slice/categorySlice';
import { fetchJobValues } from '../redux/slice/jobSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../redux/store';
import { fetchPersonelValues } from '../redux/slice/personelSlice';
import { AuthUser } from './auth.types';
import { setAuthUser } from '../redux/slice/authSlice';
import { useCurrentModule } from '../utils/common';

declare global {
  interface Window {
    newrelic: any;
  }
}

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: AuthUser | null;
  login: () => void;
  logout: () => void;
  isKeycloakReady: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  token: null,
  user: null,
  login: () => {},
  logout: () => {},
  isKeycloakReady: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const initialDataLoadedRef = useRef(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isKeycloakReady, setIsKeycloakReady] = useState(false);

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        // Login başarılı olduğunda tek bir yerden handle ediyoruz
        keycloak.onAuthSuccess = () => {
          if (!keycloak.token) return;

          const decoded = decodeJwt(keycloak.token);
          const roles =
            decoded?.realm_access?.roles ||
            decoded?.resource_access?.['isg-react-service']?.roles ||
            [];

          const authUser: AuthUser = {
            id: decoded.sub,
            username: decoded.preferred_username,
            email: decoded.email,
            name: decoded.name,
            roles,
            firmId: decoded.firmId,
            companyId: decoded.companyId,
          };

          setToken(keycloak.token);
          setUser(authUser);
          setIsAuthenticated(true);
          dispatch(setAuthUser(authUser));

          if (window.location.pathname === '/' || window.location.pathname === '/login') { // !initialDataLoadedRef.current
            initialDataLoadedRef.current = true;

            dispatch(fetchMenu());
            dispatch(fetchLovValues());
            dispatch(fetchLocationValues());
            dispatch(fetchCategoryValues());
            dispatch(fetchJobValues());

            let targetPath = '/';
            if (authUser.roles.includes('PLATFORM_ADMIN') || authUser.roles.includes('ADMIN')) {
              targetPath = '/admin/dashboard';
              dispatch(fetchPersonelValues('admin'));
            } else if (authUser.roles.includes('ISG_MANAGER') || authUser.roles.includes('ISG_REGULAR')) {
              targetPath = '/isg/home';
              dispatch(fetchPersonelValues('isg'));
            } else if (authUser.roles.includes('FIRM_MANAGER') || authUser.roles.includes('FIRM_REGULAR')) {
              targetPath = '/firm/home';
              dispatch(fetchPersonelValues('firm'));
            }

            navigate(targetPath, { replace: true });
          }
        };

        if (!(keycloak as any).didInitialize) {
          const auth = await keycloak.init({
            onLoad: 'check-sso',
            pkceMethod: 'S256',
            silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          });

          (keycloak as any).didInitialize = true;
          setIsAuthenticated(auth);

          if (keycloak.authenticated && keycloak.token && keycloak.onAuthSuccess) {
            keycloak.onAuthSuccess();
          }

          const refreshInterval = window.setInterval(() => {
            keycloak.updateToken(50).then((refreshed: boolean) => {
              if (refreshed && keycloak.token) {
                const decoded = decodeJwt(keycloak.token);

                const roles =
                  decoded?.realm_access?.roles ||
                  decoded?.resource_access?.['isg-react-service']?.roles ||
                  [];

                setToken(keycloak.token);
                setUser({
                  id: decoded.sub,
                  username: decoded.preferred_username,
                  email: decoded.email,
                  name: decoded.name,
                  roles,
                  firmId: decoded.firmId,
                  companyId: decoded.companyId,
                });
                dispatch(setAuthUser({
                  id: decoded.sub,
                  username: decoded.preferred_username,
                  email: decoded.email,
                  name: decoded.name,
                  roles,
                  firmId: decoded.firmId,
                  companyId: decoded.companyId,
                }));

              }
            })
            .catch((err: any) => {
                // If refresh fails (expired session, logout in another tab, etc.),
                // clear auth state and let the user re-login.
                // send user to login immediately
                console.warn('Keycloak token refresh failed. Redirecting to login.', err);
                setIsAuthenticated(false);
                setToken(null);
                setUser(null);
                keycloak.login();
              });
          }, 30000);

          // cleanup
          return () => window.clearInterval(refreshInterval);
        } else {
          setIsAuthenticated(!!keycloak.authenticated);

          if (keycloak.authenticated && keycloak.token && keycloak.onAuthSuccess) {
            keycloak.onAuthSuccess();
          }
        }
      } catch (e) {
        console.error('Keycloak init error', e);
        // hata olsa bile app kilitlenmesin
        setIsAuthenticated(false);
      } finally {
        // NE OLURSA OLSUN loader’dan çık
        setIsKeycloakReady(true);
      }
    };

    initKeycloak();
  }, [navigate, dispatch]);

  // Keycloak hazır olmadan login çağrılmasın
  const login = () => {
    if (!isKeycloakReady) return;
    keycloak.login();
  };

  const logout = () => keycloak.logout({ redirectUri: window.location.origin });

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, user, login, logout, isKeycloakReady }}>
      {children}
    </AuthContext.Provider>
  );
};